/* eslint-disable no-console */

import axios from 'axios';
import log from '@/logger';
import store from '@/store';
import ls from '@/helpers/localStorage';
import { checkResponseForMaintenance, checkResponseForTaxInfo, checkResponseForApiGoingOffline, checkResponseForDocumentUpload } from './utils';

const ERROR_STATUS_CODES = [500, 501, 502, 503, 504];
const UNAUTHORIZED_STATUS_CODE = 401;

// These structured failures are rendered by the transfer flow. Do not hide unrelated
// outages or malformed responses behind a blanket 503 exception.
function isHandledTransferUnavailable(error) {
  const response = error.response || {};
  const request = error.config || response.config || {};
  const data = response.data;
  const path = (request.url || '').split('?')[0];
  return response.status === 503 && (request.method || '').toLowerCase() === 'post' &&
    /(?:^|\/)api\/User\/Wallet\/(DepositBankTransfer|ConfirmDepositBankTransfer|DepositBankTransferForm)\/?$/i.test(path) &&
    data && typeof data === 'object' && !Array.isArray(data) &&
    (data.success === false || data.canDeposit === false) &&
    [11, 20, 21].includes(data.failureReason);
}

let refreshSubscribers = [];
let isRefreshing = false;

function tokenRefreshSubscribe(cb) {
  refreshSubscribers.push(cb);
}

function tokenRefreshComplete(token) {
  refreshSubscribers.map(cb => cb(token));
  refreshSubscribers = [];
}

export default (error) => {
  // Widget telemetry is best-effort; never let it raise the global error banner.
  if (error.config && error.config.bankDiagnostic) return Promise.reject(error);
  if (!error.response) {
    return Promise.reject(error);
  }
  const { config, response: { status } } = error;
  const originalRequest = config;

  // Skip token refresh for 2FA login endpoints - they use a different token mechanism
  const isTwoFactorLoginEndpoint = config.url && (
    config.url.includes('TwoFactor/SendCode') ||
    config.url.includes('TwoFactor/Verify')
  );
  const hasRefreshToken = !!ls.get('refreshToken', null);

  if (status === UNAUTHORIZED_STATUS_CODE && !isTwoFactorLoginEndpoint && hasRefreshToken) {
    log.info('[TOKEN] Unauthorized');
    if (!isRefreshing) {
      isRefreshing = true;
      const payload = {
        grant_type: 'refresh_token',
        refresh_token: ls.get('refreshToken'),
      };
      store.dispatch('user/login', payload)
        .then(() => {
          isRefreshing = false;
          log.info('[TOKEN] REFRESHED');
          tokenRefreshComplete(ls.get('token'));
        })
        .catch(() => {
          isRefreshing = false;
          log.info('[TOKEN] CANNOT REFRESH');
          store.dispatch('user/clearAuthSession');
          tokenRefreshComplete(null);
        });
    }

    return new Promise((resolve, reject) => {
      tokenRefreshSubscribe((token) => {
        if (!token) {
          reject(error);
          return;
        }
        originalRequest.headers.Authorization = `Bearer ${token}`;
        log.info('[TOKEN] Retry');
        resolve(axios(originalRequest));
      });
    });
  }

  if (error.response) {
    checkResponseForApiGoingOffline(error.response);
    checkResponseForMaintenance(error.response);
    checkResponseForTaxInfo(error.response);
    checkResponseForDocumentUpload(error.response);
    log.error('API - ERROR', error.response.status, error.response.config.url);
    if (ERROR_STATUS_CODES.indexOf(error.response.status) !== -1 &&
      !isHandledTransferUnavailable(error)) {
      store.dispatch('app/setServerError', true);
    }
  } else if (error.request) {
    log.error('The request was made but no response was received');
  } else {
    log.error('Something happened in setting up the request that triggered an error');
  }

  return Promise.reject(error);
};



// WEBPACK FOOTER //
// ./src/services/interceptors/responseError.js